"use client";

import Image from "next/image";
import { useState } from "react";
import { Autoplay, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import HeroSearch from "./HeroSearch";

import "swiper/css";
import "swiper/css/effect-fade";

/**
 * Photos behind the hero (dashboard «صور أعلى الصفحة الرئيسية»):
 * 1 photo = static, 2+ = auto-playing fade slider. Purely decorative — it
 * never takes pointer events, so every tap lands on the title / search bar.
 */
function HeroBackdrop({ slides, isAr }: { slides: string[]; isAr: boolean }) {
  const [index, setIndex] = useState(0);

  const photo = (src: string, i: number) => (
    <Image
      src={src}
      alt=""
      fill
      sizes="100vw"
      unoptimized
      draggable={false}
      loading={i === 0 ? "eager" : "lazy"}
      fetchPriority={i === 0 ? "high" : "auto"}
      className="object-cover select-none"
    />
  );

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-shade">
      {slides.length === 1 ? (
        photo(slides[0], 0)
      ) : (
        <Swiper
          key={(isAr ? "rtl-" : "ltr-") + slides.length} // Swiper reads direction/loop once
          dir={isAr ? "rtl" : "ltr"}
          modules={[Autoplay, EffectFade]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          loop
          speed={1200}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          allowTouchMove={false}
          onSlideChange={(s) => setIndex(s.realIndex)}
          className="h-full w-full"
        >
          {slides.map((src, i) => (
            <SwiperSlide key={src + i} className="relative h-full! w-full">
              {photo(src, i)}
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Keeps the white title readable on any photo */}
      <div className="absolute inset-0 z-1 bg-gradient-to-b from-shade/60 via-shade/35 to-shade/70" />

      {slides.length > 1 && (
        <div className="absolute inset-x-0 bottom-4 z-2 flex items-center justify-center gap-1.5">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`rounded-full bg-white shadow transition-all duration-300 ${i === index ? "h-1.5 w-4 opacity-100" : "size-1.5 opacity-60"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Hero({
  isAr,
  heroTitle,
  heroSubtitle,
  slides = [],
}: {
  isAr: boolean;
  heroImg: string;
  heroTitle: string;
  heroSubtitle: string;
  slides?: string[];
}) {
  const locale = isAr ? "ar" : "en";
  const photos = slides.filter(Boolean);
  const hasPhotos = photos.length > 0;

  return (
    <section
      className={`relative w-full flex items-center justify-center ${
        hasPhotos ? "min-h-[520px] md:min-h-[600px] py-12 md:py-20" : "py-10 md:py-20"
      }`}
    >
      {hasPhotos && <HeroBackdrop slides={photos} isAr={isAr} />}

      {/* Own layer above the slider: iOS Safari hit-tests translate3d layers
          above z-indexed siblings, so the content gets its own GPU layer too. */}
      <div className={`relative z-10 text-center px-5 max-w-4xl w-full ${hasPhotos ? "transform-gpu" : ""}`}>
        <h1
          className={`font-display najdi-fade-up text-[32px] sm:text-4xl md:text-[52px] font-extrabold mb-3 leading-tight ${
            hasPhotos ? "text-white drop-shadow-lg" : "text-ink-900"
          }`}
          style={{ animationDelay: "100ms" }}
        >
          {heroTitle}
        </h1>
        <p
          className={`najdi-fade-up text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-6 md:mb-8 ${
            hasPhotos ? "text-white/85 drop-shadow" : "text-ink-900/70"
          }`}
          style={{ animationDelay: "200ms" }}
        >
          {heroSubtitle}
        </p>

        {/* Search bar */}
        <div
          className="relative max-w-4xl mx-auto najdi-fade-up"
          style={{ animationDelay: "300ms" }}
        >
          <HeroSearch locale={locale} isAr={isAr} />
        </div>
      </div>
    </section>
  );
}
